import { exhaustiveFail } from '@cbridge-utils/error';
import {
  IApiRuleValidationError, ApiRuleErrorCode, IApiRuleWithError, IApiRuleWithWarning, IApiBasicRule, IApiClientRule, IApiBasicRuleUpdate,
  IApiClientRuleUpdate, ApiRuleErrorKey, IApiRuleWarnings, ApiRuleWarningKey, ApiRuleWarningNoMapping,
} from '@cbridge-packages/api';
import {
  RuleValidationError, RuleErrorCode, IRuleValidationResult, IBasicRule, IClientRule, RuleErrorKey,
  IRuleWarnings, RuleWarningType, RuleWarningKey, RuleWarningNoMapping,
} from './rule.interface';

export namespace RuleTranslator {
  export function fromApiRuleValidationError(error: IApiRuleValidationError): RuleValidationError {
    return {
      code: RuleTranslator.fromApiRuleErrorCode(error.code),
      key: RuleTranslator.fromApiRuleErrorKey(error.key),
      ref: error.ref,
    };
  }

  export function fromApiRuleErrorCode(code: ApiRuleErrorCode): RuleErrorCode {
    if (code === ApiRuleErrorCode.NotFound) {
      return RuleErrorCode.NotFound;
    }

    return undefined;
  }

  export function fromApiRuleErrorKey(key: ApiRuleErrorKey): RuleErrorKey {
    switch (key) {
      case ApiRuleErrorKey.CoreSymbol:
        return RuleErrorKey.CoreSymbol;
      case ApiRuleErrorKey.ClientSymbol:
        return RuleErrorKey.ClientSymbol;
      case ApiRuleErrorKey.ProfileId:
        return RuleErrorKey.ProfileId;
      case ApiRuleErrorKey.ServerId:
        return RuleErrorKey.ServerId;
      case ApiRuleErrorKey.StreamId:
        return RuleErrorKey.StreamId;
      case ApiRuleErrorKey.SymbolGroup:
        return RuleErrorKey.SymbolGroup;
      default:
        return exhaustiveFail(key);
    }
  }

  export function fromApiRuleWarningKey(key: ApiRuleWarningKey): RuleWarningKey {
    switch (key) {
      case ApiRuleWarningKey.StreamId:
        return RuleWarningKey.StreamId;
      case ApiRuleWarningKey.SymbolGroup:
        return RuleWarningKey.SymbolGroup;
      case ApiRuleWarningKey.CoreSymbol:
        return RuleWarningKey.CoreSymbol;
      case ApiRuleWarningKey.ClientSymbol:
        return RuleWarningKey.ClientSymbol;
      default:
        return exhaustiveFail(key);
    }
  }

  export function fromApiRuleWarningNoMapping(noMapping: ApiRuleWarningNoMapping): RuleWarningNoMapping {
    switch (noMapping) {
      case ApiRuleWarningNoMapping.ServerId:
        return RuleWarningNoMapping.ServerId;
      case ApiRuleWarningNoMapping.StreamId:
        return RuleWarningNoMapping.StreamId;
      case ApiRuleWarningNoMapping.CoreSymbol:
        return RuleWarningNoMapping.CoreSymbol;
      case ApiRuleWarningNoMapping.ClientSymbol:
        return RuleWarningNoMapping.ClientSymbol;
      default:
        return exhaustiveFail(noMapping);
    }
  }

  export function fromApiRuleWarnings(warnings: IApiRuleWarnings): IRuleWarnings {
    if (!warnings) {
      return undefined;
    }

    if (RuleWarningType.Unused in warnings) {
      return { unused: warnings.unused };
    }

    if (RuleWarningType.Useless in warnings) {
      return {
        useless: warnings.useless.map((item) => ({
          key: RuleTranslator.fromApiRuleWarningKey(item.key),
          noMapping: RuleTranslator.fromApiRuleWarningNoMapping(item.noMapping),
        })),
      };
    }

    return undefined;
  }

  export function fromApiValidation(validation: IApiRuleWithError & IApiRuleWithWarning): IRuleValidationResult {
    const { index, errors, warnings } = validation;

    return {
      index,
      errors: errors?.map(RuleTranslator.fromApiRuleValidationError),
      warnings: RuleTranslator.fromApiRuleWarnings(warnings),
    };
  }

  export function fromApiBasicRule(rule: IApiBasicRule): IBasicRule {
    const {
      profile, server, stream, coreSymbols, symbolGroup, errors, warnings,
    } = rule;

    return {
      profile: {
        value: profile.id,
        label: profile.name,
      },
      server: server && {
        value: server.id,
        label: server.name,
      },
      stream: stream && {
        value: stream.id,
        label: stream.name,
      },
      coreSymbols: coreSymbols?.map(({ id: value, name: label }) => ({ value, label })),
      symbolGroup,
      errors: errors?.map(RuleTranslator.fromApiRuleValidationError),
      warnings: RuleTranslator.fromApiRuleWarnings(warnings),
    };
  }

  export function fromApiClientRule(rule: IApiClientRule): IClientRule {
    const result: IClientRule = RuleTranslator.fromApiBasicRule(rule);
    const { clientSymbols } = rule;

    if (clientSymbols?.length > 0) {
      result['clientSymbols'] = clientSymbols?.map((name) => ({ value: name, label: name }));
    }

    return result;
  }

  export function toApiBasicRule(rule: IBasicRule): IApiBasicRuleUpdate {
    return {
      profileId: rule.profile.value,
      coreSymbols: rule.coreSymbols?.map(({ value }) => value),
      serverId: rule.server?.value,
      streamId: rule.stream?.value,
      symbolGroup: rule.symbolGroup,
    };
  }

  export function toApiClientRule(rule: IClientRule): IApiClientRuleUpdate {
    const result: IApiClientRuleUpdate = RuleTranslator.toApiBasicRule(rule);
    const { clientSymbols } = rule;

    if (clientSymbols?.length > 0) {
      result['clientSymbols'] = clientSymbols?.map(({ value }) => value);
    }

    return result;
  }
}
